import { cn } from '@/lib/cn'

/**
 * Ilustrasi digambar sebagai SVG sebaris, bukan berkas gambar.
 *
 * Alasannya dua: warnanya mengikuti token tema sehingga ikut berubah pada
 * mode gelap, dan tidak ada permintaan jaringan tambahan pada halaman yang
 * justru paling sering menjadi kesan pertama.
 */

/** Alur escrow: dana Klien tertahan sejenak sebelum sampai ke Tenaga Ahli. */
export function EscrowIllustration({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 320 180" className={cn('w-full', className)} role="img" aria-label="Alur dana escrow">
      <defs>
        <linearGradient id="escrow-flow" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="var(--proyek)" />
          <stop offset="50%" stopColor="var(--kelas)" />
          <stop offset="100%" stopColor="var(--lowongan)" />
        </linearGradient>
      </defs>

      <path
        d="M52 90 H268"
        stroke="url(#escrow-flow)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="10 10"
        opacity="0.6"
      />

      {/* Klien */}
      <circle cx="52" cy="90" r="26" fill="var(--proyek-soft)" stroke="var(--proyek)" strokeWidth="2" />
      <circle cx="52" cy="82" r="8" fill="var(--proyek)" />
      <path d="M38 104c3-8 8-12 14-12s11 4 14 12" fill="var(--proyek)" />

      {/* Brankas escrow di tengah */}
      <rect x="126" y="56" width="68" height="68" rx="16" fill="var(--kelas-soft)" stroke="var(--kelas)" strokeWidth="2" />
      <rect x="148" y="82" width="24" height="20" rx="4" fill="var(--kelas)" />
      <path d="M154 82v-6a6 6 0 0 1 12 0v6" fill="none" stroke="var(--kelas)" strokeWidth="3" strokeLinecap="round" />

      {/* Tenaga ahli */}
      <circle cx="268" cy="90" r="26" fill="var(--lowongan-soft)" stroke="var(--lowongan)" strokeWidth="2" />
      <circle cx="268" cy="82" r="8" fill="var(--lowongan)" />
      <path d="M254 104c3-8 8-12 14-12s11 4 14 12" fill="var(--lowongan)" />

      {/* Koin yang bergerak sepanjang alur */}
      <circle r="7" fill="var(--sayembara)">
        <animateMotion dur="4s" repeatCount="indefinite" path="M78 90 H126" />
      </circle>
      <circle r="7" fill="var(--sayembara)" opacity="0.85">
        <animateMotion dur="4s" begin="2s" repeatCount="indefinite" path="M194 90 H242" />
      </circle>
    </svg>
  )
}

/** Keadaan kosong: kotak terbuka dengan sedikit kilau. */
export function EmptyBoxIllustration({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 160 120" className={cn('w-40', className)} role="img" aria-label="Belum ada data">
      <ellipse cx="80" cy="102" rx="46" ry="7" fill="var(--border)" opacity="0.7" />
      <path d="M36 54h88l-8 44a6 6 0 0 1-6 5H50a6 6 0 0 1-6-5L36 54Z" fill="var(--brand-soft)" stroke="var(--brand)" strokeWidth="2" strokeLinejoin="round" />
      <path d="M30 40h100v14H30z" fill="var(--brand)" opacity="0.15" stroke="var(--brand)" strokeWidth="2" />
      <path d="M64 26l6 10M96 26l-6 10" stroke="var(--brand)" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
      <circle cx="80" cy="20" r="3.5" fill="var(--sayembara)" />
    </svg>
  )
}

/** Perayaan: piala dengan percikan, dipakai saat kelas tuntas atau menang sayembara. */
export function CelebrationIllustration({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 160 130" className={cn('w-40', className)} role="img" aria-label="Selamat">
      <path d="M56 30h48v22a24 24 0 0 1-48 0V30Z" fill="var(--sayembara-soft)" stroke="var(--sayembara)" strokeWidth="2.5" />
      <path d="M56 36H44a12 12 0 0 0 12 12M104 36h12a12 12 0 0 1-12 12" fill="none" stroke="var(--sayembara)" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M74 76h12v12H74z" fill="var(--sayembara)" />
      <path d="M62 92h36a4 4 0 0 1 4 4v6H58v-6a4 4 0 0 1 4-4Z" fill="var(--sayembara)" opacity="0.85" />
      <g stroke="var(--kelas)" strokeWidth="2.5" strokeLinecap="round">
        <path d="M32 22l6 6M128 22l-6 6M28 58h8M124 58h8" />
      </g>
      <circle cx="40" cy="40" r="3" fill="var(--jasa)" />
      <circle cx="120" cy="44" r="3" fill="var(--talenta)" />
      <circle cx="80" cy="14" r="3.5" fill="var(--lowongan)" />
    </svg>
  )
}

/** Kurva pertumbuhan sederhana untuk bagian statistik. */
export function GrowthIllustration({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 200 100" className={cn('w-full', className)} role="img" aria-label="Pertumbuhan">
      <defs>
        <linearGradient id="growth-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--lowongan)" stopOpacity="0.35" />
          <stop offset="100%" stopColor="var(--lowongan)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d="M6 84c22 0 30-18 48-26s28 10 46-8 30-30 46-34v76H6Z" fill="url(#growth-fill)" />
      <path
        d="M6 84c22 0 30-18 48-26s28 10 46-8 30-30 46-34"
        fill="none"
        stroke="var(--lowongan)"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <circle cx="146" cy="16" r="5" fill="var(--lowongan)" />
    </svg>
  )
}
